import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit, inject } from '@angular/core';
import { NavigationStart } from '@angular/router';
import { Category, ICategoryDataV3 } from '@df/catalog/category/category';
import { App } from '@df/core/app';
import { MetaState } from '@df/core/meta/meta-state';
import { FacetOverridePipe } from '@df/module-facet-url/util/facet-override.pipe';
import { DfImageDirective } from '@df/module-image/df-image.directive';
import { CustomerModel } from '@df/session/api/customer.model';
import { DfModalService } from '@df/ui/modal/df-modal.service';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsComponent, ICmsContent, NICmsContentComponent } from '@icms/core/content';
import { NICmsDataChange } from '@icms/core/data/change/icms-data-change.service';
import { getBlockUrl } from '@icms/core/resource/block/get-block-url';
import { NICmsBlock } from '@icms/core/resource/block/icms-block';
import { AbstractCmsBlockComponent } from '../../../../cms/block/abstract-cms-block.component';
import { isCustomerAllowed } from '../../../../customer/groups/customer-groups';
import { PageHeaderService } from '../../../../layout/page/header/page-header.service';
import { NAccessRestrictionsModal } from '../../page/access/access-restrictions-modal.interface';
import './category-header.config';
import { CATEGORY_HEADER_CONFIG } from './category-header.config';
import { NCategoryHeader } from './category-header.interface';

@Component({
    selector: 'category-header',
    templateUrl: './category-header.html',
    styleUrl: './category-header.component.scss',
    standalone: true,
    imports: [CommonModule, DfImageDirective, FacetOverridePipe, ICmsClientModule],
    changeDetection: ChangeDetectionStrategy.OnPush
})
@ICmsComponent<CategoryHeaderComponent>(CATEGORY_HEADER_CONFIG)
export class CategoryHeaderComponent extends AbstractCmsBlockComponent implements NICmsContentComponent.IComponent, OnInit, OnDestroy {
    @ICmsContent({
        type: 'blockContent'
    })
    override content!: NCategoryHeader.IData;

    @Input()
    category!: Category<ICategoryDataV3>;

    protected override asyncCd = false;
    protected pageHeaderTransparent = false;

    protected app = inject(App);
    private pageHeaderService = inject(PageHeaderService);
    private dfModalService = inject(DfModalService);
    private customerModel = inject(CustomerModel);
    private previousMetaState: MetaState | undefined;

    override ngOnInit() {
        super.ngOnInit();

        this.subscriptions.add(
            this.iCmsDataChangeService.afterChange$.subscribe(event => {
                if (
                    (<NICmsBlock.IData>event.model.data).url === getBlockUrl(NCategoryHeader.ID, this.variant) &&
                    event.changeType === NICmsDataChange.EChangeType.change
                ) {
                    this.updateClassNames();

                    this.detectChanges();
                }
            })
        );

        this.subscriptions.add(
            this.app.meta.subscribe(() => {
                this.timeoutService.setTimeout(() => {
                    this.openAccessRestrictionsModal();
                    this.previousMetaState = this.app.meta.state;
                }, 500);
            })
        );

        if (this.app.meta.state !== this.previousMetaState) {
            this.openAccessRestrictionsModal();
        }

        this.subscriptions.add(
            this.app.meta.routerEvents.subscribe((event) => {
                if (event instanceof NavigationStart) {
                    this.detectChanges();
                }
            })
        );

        this.subscriptions.add(
            this.pageHeaderService.getState().subscribe(state => {
                if (this.pageHeaderTransparent !== state?.transparent) {
                    this.pageHeaderTransparent = !!state?.transparent;
                    this.detectChanges();
                }
            })
        );

        this.updateClassNames();
    }

    openAccessRestrictionsModal() {
        const visibility = this.content?.visibility;
        this.timeoutService.setTimeout(() => {
            if (this.content?.enableAccessRestriction && !isCustomerAllowed(visibility, this.customerModel.data.group_id)) {
                this.dfModalService.open(undefined, NAccessRestrictionsModal.ID, undefined, this.content);
            }
        }, 500);

        this.detectChanges();
    }

    override ngOnDestroy() {
        super.ngOnDestroy();
        this.timeoutService.platformSafe(() => {
            this.pageHeaderService.resetHighPriorityState();
        }); // injector may be destroyed
    }

    protected updateClassNames(): void {
        // fallback to old saved data with `header_` prefix
        const transparent =
            this.content?.headerTransparent === undefined ? (<any>this.content)?.header_transparent : this.content?.headerTransparent;
        const lightMode =
            this.content?.headerLightMode === undefined ? (<any>this.content)?.header_light_mode : this.content?.headerLightMode;
        const hideUp = this.content?.headerHideUp === undefined ? (<any>this.content)?.header_hide_up : this.content?.headerHideUp;

        this.pageHeaderService.setHighPriorityState({
            transparent,
            lightMode,
            hideUp
        });
    }
}
